import { get, patch, postJson } from './apiClient';
import type { CreateCatalogueItemPayload } from './masterCatalogueApi';

/**
 * The catalogue addition review queue.
 *
 * A user pricing a quotation who finds an item Master Catalogue has never heard
 * of types its details and submits them here instead of the catalogue silently
 * gaining a row nobody reviewed. An admin (`catalogue_review` screen) approves —
 * as submitted, or edited first — or rejects with a reason. Approving is the
 * only thing that ever creates the real `MasterCatalogueItem` row; submitting
 * does not.
 *
 * Server: `POST/GET /api/catalogue-submissions`, `PATCH
 * /api/catalogue-submissions/:id/decision`, `GET
 * /api/catalogue-submissions/quotation/:quotationId`. `submitCatalogueAddition`
 * is called from the quotation line's "+ Add this item to Master Catalogue"
 * (`features/quotations/ItemLookupPanel`), and `getSubmissionsForQuotation`
 * from that page's own `useCatalogueSubmissionMarkers` hook — the pending /
 * rejected marks a submission leaves on the line it was raised from.
 */

/** `held`: added from a quotation line, waiting for that quotation to be sent or finalized. */
export type CatalogueSubmissionStatus = 'held' | 'pending' | 'approved' | 'rejected';

/**
 * Who submitted or decided a row. The server may return a populated user (an
 * object) or, for an actor it cannot resolve, a bare id string — this reads
 * either without guessing a shape it was never told.
 */
export type CatalogueSubmissionActor =
  | string
  | { _id?: string; name?: string; firstName?: string; lastName?: string; email?: string }
  | null;

/** Where a submission came from, when it came from a quotation line. */
export interface CatalogueSubmissionRequestedFrom {
  quotationId?: string | null;
  quotationNumber?: string | null;
  lineIndex?: number | null;
  lineItemName?: string | null;
}

/**
 * One existing catalogue row a PENDING submission's `likelyMatches` offers as a
 * possible duplicate — a lean projection, not a full `MasterCatalogueItem`.
 */
export interface CatalogueSubmissionLikelyMatch {
  _id: string;
  item_name: string;
  unit?: string | null;
  supplier?: string | null;
  item_price?: number | null;
  currency?: string | null;
}

export interface CatalogueSubmission {
  _id: string;
  status: CatalogueSubmissionStatus;
  payload: CreateCatalogueItemPayload;
  requestedFrom?: CatalogueSubmissionRequestedFrom | null;
  submittedBy?: CatalogueSubmissionActor;
  submittedAt?: string;
  decidedBy?: CatalogueSubmissionActor;
  decidedAt?: string | null;
  decisionReason?: string | null;
  /** Set once approved — the row the decision created. */
  createdItemId?: string | null;
  identityKey?: string | null;
  /**
   * Up to 5 existing catalogue rows that may be the same product — how an admin
   * refuses a near-duplicate instead of approving a second row for a product
   * already in the catalogue. PENDING submissions only.
   */
  likelyMatches?: CatalogueSubmissionLikelyMatch[];
}

export interface ListCatalogueSubmissionsResponse {
  success: boolean;
  data: {
    submissions: CatalogueSubmission[];
    page: number;
    limit: number;
    total: number;
  };
}

export async function listCatalogueSubmissions(
  params: { status?: CatalogueSubmissionStatus; page?: number; limit?: number } = {}
): Promise<ListCatalogueSubmissionsResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  qs.set('page', String(params.page || 1));
  qs.set('limit', String(params.limit || 20));
  return get<ListCatalogueSubmissionsResponse>(`/catalogue-submissions?${qs.toString()}`);
}

export type CatalogueSubmissionDecision = 'approve' | 'reject';

export interface DecideCatalogueSubmissionBody {
  decision: CatalogueSubmissionDecision;
  /** Required, non-empty, for a reject — the server refuses an empty one (CATALOGUE_SUBMISSION_REASON_REQUIRED). */
  reason?: string;
  /** The edit-then-approve path. Validated exactly as a submit is (CATALOGUE_SUBMISSION_INVALID on a bad field). */
  payloadOverride?: CreateCatalogueItemPayload;
}

export interface DecideCatalogueSubmissionResponse {
  success: boolean;
  data: CatalogueSubmission & {
    /**
     * Approve only: what happened to the quotation line the addition came from
     * (server owner `linkOutcome`). 'not_linked' = nothing retries it; the line's
     * refresh links it.
     */
    quotationLineLinkOutcome?: 'linked' | 'fine' | 'not_linked';
  };
  message?: string;
}

export async function decideCatalogueSubmission(
  id: string,
  body: DecideCatalogueSubmissionBody
): Promise<DecideCatalogueSubmissionResponse> {
  return patch<DecideCatalogueSubmissionResponse>(`/catalogue-submissions/${id}/decision`, body);
}

export interface SubmitCatalogueAdditionBody {
  payload: CreateCatalogueItemPayload;
  requestedFrom: CatalogueSubmissionRequestedFrom;
}

export interface SubmitCatalogueAdditionResponse {
  success: boolean;
  data: CatalogueSubmission & {
    /**
     * The VAT inside the typed price grossed by its VAT box at the Settings
     * rate (server owner `catalogueRowCostVatPercentOrNull`): 0 = No, null =
     * nothing added / no rate set. A receipt fact, never stored on the
     * submission; the quotation line it prices records it (D82).
     */
    costVatPercent?: number | null;
  };
}

/**
 * A pricer types a catalogue addition, from a quotation line, and it queues
 * for review. Validated exactly as `POST /api/master-catalogue` validates a
 * real row (`catalogueSubmission.service.submitCatalogueAddition`), so a
 * payload that could never become a catalogue row is refused when it is
 * typed rather than weeks later when an admin approves it.
 *
 * The caller handles these by CODE, never by matching the server's prose
 * (invariant 6):
 *   - `CATALOGUE_SUBMISSION_ITEM_EXISTS` — already in the catalogue; the
 *     thrown error's `message` already names the row, and its (unparsed by
 *     `apiClient`) details carry `existingItemId`/`existingItemName`.
 *   - `CATALOGUE_SUBMISSION_ALREADY_PENDING` — somebody already submitted it.
 *   - `CATALOGUE_SUBMISSION_INVALID` — per-field errors; `CatalogueItemForm`'s
 *     own `parseServerError` already knows how to display those.
 */
export async function submitCatalogueAddition(
  body: SubmitCatalogueAdditionBody
): Promise<SubmitCatalogueAdditionResponse> {
  return postJson<SubmitCatalogueAdditionResponse>('/catalogue-submissions', body);
}

/**
 * Every submission (any status) raised from one quotation, shaped minimally
 * for the grid's own line markers — see
 * `features/quotations/useCatalogueSubmissionMarkers`. Fetch ONCE per
 * quotation load; nothing about a submission is denormalized onto a
 * quotation line (`catalogueSubmission.service.getSubmissionsForQuotation`'s
 * own header says why).
 */
export interface QuotationCatalogueSubmissionMarker {
  _id: string;
  status: CatalogueSubmissionStatus;
  decisionReason: string | null;
  lineIndex: number | null;
  itemName: string | null;
}

export interface GetSubmissionsForQuotationResponse {
  success: boolean;
  data: QuotationCatalogueSubmissionMarker[];
}

export async function getSubmissionsForQuotation(
  quotationId: string
): Promise<GetSubmissionsForQuotationResponse> {
  return get<GetSubmissionsForQuotationResponse>(`/catalogue-submissions/quotation/${quotationId}`);
}

/**
 * The sidebar badge's catalogue-addition count.
 *
 * There is no count endpoint, and fetching the whole pending page just to read
 * its length would grow with the queue for no reason — `total` on a 1-row page
 * is the same number the list screen itself reads it off. Packing alerts are a
 * separate queue with their own total (`packingAlertsApi.listPackingAlerts`);
 * the sidebar adds the two.
 */
export async function countPendingReviews(): Promise<number> {
  const res = await listCatalogueSubmissions({ status: 'pending', page: 1, limit: 1 });
  return res.data?.total ?? 0;
}

/**
 * Dispatched after a submission is approved/rejected, or a packing alert is
 * marked checked, so the sidebar's Review badge refreshes without polling —
 * the same `window.dispatchEvent` + `addEventListener` shape `apiClient.ts`
 * already uses for `session-expired`. No payload: every listener re-fetches
 * its own counts rather than trusting a number carried on the event.
 */
export const REVIEW_QUEUE_CHANGED_EVENT = 'usc:review-queue-changed';

export function notifyReviewQueueChanged(): void {
  window.dispatchEvent(new CustomEvent(REVIEW_QUEUE_CHANGED_EVENT));
}
